import React from "react"
import { Button } from "@quorumus/plum"
import { Modal, createStyles } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { Icon } from "@/components"
import { notifications } from "@mantine/notifications"
import { QuorumTheme } from "app/static/frontend/design-constants"

const REMOVE_ROW_MODAL_CONFIG = {
    title: "Delete row?",
    text: "This action cannot be undone",
    submitText: "Delete",
    cancelText: "Keep",
}

const removeRowNotificationStyles = (theme) => ({
    root: {
        backgroundColor: theme.colors.gray[7],
        padding: "10px 12px", // vertically balanced
    },
    title: {
        color: "white",
        margin: 0,
    },
    body: {
        display: "flex",
        alignItems: "center", // center items vertically
        alignSelf: "center",
        padding: 0,
    },
    icon: {
        backgroundColor: "#00CE28",
        color: `${theme.colors.gray[7]}`,
    },
    closeButton: {
        marginTop: "2.5%",
        marginRight: "2%",
        height: "auto",
        width: "auto",
        "&:hover": {
            backgroundColor: "rgba(173, 185, 189, 0.3)",
        },
    },
})

const useStyles = createStyles((theme) => ({
    removeRowModal: {
        "& .mantine-Modal-content": {
            borderRadius: "16px",
            border: `1px solid ${theme.colors.gray[2]}`,
            boxShadow: "0px 4px 12px -4px #00000033",
            padding: "0.6% 0%",
        },
    },
    removeRowModalTitle: {
        fontSize: "18px",
        fontWeight: 500,
        color: `${theme.colors.gray[9]}`,
        marginTop: "6%",
    },

    removeRowModalText: {
        fontSize: "16px",
        fontWeight: 400,
        color: `${theme.colors.gray[7]}`,
        marginTop: "0.8%",
    },

    removeRowContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        "& div": {
            marginTop: "6.2%",
            width: "100%",
            display: "flex",
            justifyContent: "center",
        },
        "& button": {
            marginRight: "2%",
            fontSize: "15px",
            padding: "0% 5%",
        },
    },

    removeRowButton: {
        boxShadow: "none !important",
    },

    removeRowModalIcon: {
        backgroundColor: "#FFC6CA !important",
        borderRadius: "50% !important",
        width: "10% !important",
        height: "10% !important",
        padding: "3% !important",
    },

    deleteRowSubmit: {
        "&:focus": {
            color: `${theme.colors.red[9]} !important`,
            boxShadow: "none !important",
            backgroundColor: "transparent !important",
            outline: "none !important",
            border: "none !important",
        },
        "&:hover": {
            backgroundColor: `${theme.colors.red[0]} !important`,
        },
    },
}))

export const RemoveRowButton: React.FC<RemoveRowButtonProps> = ({ datumId, removeRow, id, dataPlumId, ...props }) => {
    const [opened, { open, close }] = useDisclosure(false)

    const { classes, cx } = useStyles()

    const handleClick = async () => {
        open() // Open the modal
    }

    const handleConfirm = async () => {
        removeRow(id, datumId)
        close() // Close the modal
        notifications.show({
            id: `remove-row-notification-${datumId}`,
            title: "Row deleted",
            styles: removeRowNotificationStyles,
            icon: <Icon icon="check" />,
            autoClose: true,
            withCloseButton: true,
            radius: "md",
            message: "",
        })
    }

    const handleCancel = () => {
        close() // Close the modal
        notifications.hide(`remove-row-notification-${datumId}`)
    }

    return (
        <>
            <Button
                variant="tertiary"
                onClick={handleClick}
                data-plum-id={dataPlumId}
                leftSection={<Icon icon="trash" />}
                className={cx(classes.removeRowButton)}
                {...props}
            ></Button>
            <Modal
                opened={opened}
                onClose={close}
                size="md"
                withCloseButton={false}
                className={cx(classes.removeRowModal)}
                centered
            >
                <div className={cx(classes.removeRowContainer)}>
                    <Icon
                        icon="trash"
                        color={QuorumTheme.colors.red[9]}
                        size="sm"
                        className={cx(classes.removeRowModalIcon)}
                    />
                    <p className={cx(classes.removeRowModalTitle)}>{REMOVE_ROW_MODAL_CONFIG.title}</p>
                    <p className={cx(classes.removeRowModalText)}>{REMOVE_ROW_MODAL_CONFIG.text}</p>
                    <div>
                        <Button
                            className={cx(classes.deleteRowSubmit)}
                            danger={true}
                            onClick={handleConfirm}
                            size="md"
                            variant="tertiary"
                        >
                            {REMOVE_ROW_MODAL_CONFIG.submitText}
                        </Button>
                        <Button variant="primary" size="md" onClick={handleCancel}>
                            {REMOVE_ROW_MODAL_CONFIG.cancelText}
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    )
}

interface RemoveRowButtonProps {
    datumId: number | string
    removeRow: (id: string, datumId: number | string) => void
    id: string
    dataPlumId?: string
    rowIndex?: number
    rows?: any[]
}
